import { Link } from "react-router-dom";
import TopBar from "@/components/feature/TopBar";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { services } from "@/mocks/updiag";
import {
  schemaLocalBusiness,
  schemaBreadcrumb,
  renderSchema,
} from "@/lib/schemas";

const PACKS_SLUGS = new Set(["pack-vente", "pack-location"]);

export default function DiagnosticsPage() {
  const packs = services.filter((s) => PACKS_SLUGS.has(s.slug));
  const unitServices = services.filter((s) => !PACKS_SLUGS.has(s.slug));

  return (
    <>
      <title>
        Nos Diagnostics Immobiliers Paris & IDF — DPE, Amiante, Plomb, Pack Vente | Up Diag
      </title>
      <meta
        name="description"
        content="Tous vos diagnostics immobiliers obligatoires à Paris et en Île-de-France : DPE, Pack Vente, Pack Location, Amiante, Plomb, Électricité, Gaz, Termites, Loi Carrez. Certifié COFRAC, RDV 48h, dès 80 €."
      />
      <meta
        name="keywords"
        content="diagnostic immobilier Paris, DPE Paris, pack vente immobilier, pack location, diagnostic amiante IDF, CREP plomb Paris, diagnostiqueur certifié Cofrac"
      />
      <link rel="canonical" href="https://updiag.fr/diagnostics" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={renderSchema(schemaLocalBusiness())}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={renderSchema(
          schemaBreadcrumb([
            { name: "Accueil", url: "/" },
            { name: "Nos diagnostics", url: "/diagnostics" },
          ])
        )}
      />
      <TopBar />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/img/readdy/diag-hero.jpg"
              alt="Diagnostics immobiliers obligatoires Paris & Île-de-France"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-[#0F3B5F]/85"></div>
          </div>
          <div className="relative max-w-4xl mx-auto px-6 text-center">
            <span className="inline-block bg-white/15 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
              Tous nos services
            </span>
            <h1
              className="text-4xl md:text-5xl font-extrabold text-white mb-5"
              style={{ fontFamily: "Barlow, sans-serif" }}
            >
              Nos diagnostics immobiliers
              <br />
              <span className="text-[#F97316]">à Paris &amp; Île-de-France</span>
            </h1>
            <p className="text-lg text-white/85 max-w-2xl mx-auto mb-8">
              Certifiés COFRAC, nos diagnostiqueurs réalisent l'ensemble des diagnostics obligatoires
              pour la vente et la location. <strong>Intervention sous 48h, rapport le jour même.</strong>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/devis"
                className="flex items-center justify-center gap-2 px-7 py-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap"
              >
                <i className="ri-file-list-3-line"></i> Obtenir mon devis gratuit
              </Link>
              <Link
                to="/tarifs"
                className="flex items-center justify-center gap-2 px-7 py-4 border-2 border-white text-white rounded-xl font-bold hover:bg-white/10 transition-all whitespace-nowrap"
              >
                <i className="ri-price-tag-3-line"></i> Voir les tarifs
              </Link>
            </div>
          </div>
        </section>

        {/* Intro SEO */}
        <section className="bg-white py-16">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-8">
              <span className="inline-block bg-blue-50 text-[#0F3B5F] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
                Guichet unique certifié
              </span>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-[#0F3B5F] mb-4"
                style={{ fontFamily: "Barlow, sans-serif" }}
              >
                Tous vos diagnostics immobiliers en un seul interlocuteur
              </h2>
            </div>
            <div className="text-gray-700 leading-relaxed space-y-4 text-[15px]">
              <p>
                Up Diag est votre diagnostiqueur immobilier de référence à <strong>Paris et dans toute l'Île-de-France</strong>.
                Nous réalisons en propre le <strong>Diagnostic de Performance Énergétique (DPE)</strong>, service phare de
                notre activité, et coordonnons l'ensemble des autres diagnostics obligatoires via un réseau de
                partenaires tous <strong>certifiés Cofrac</strong> et assurés en responsabilité civile professionnelle.
              </p>
              <p>
                Que vous soyez propriétaire vendeur, bailleur, agent immobilier ou gestionnaire de copropriété,
                nous proposons deux formules packagées — le <strong>Pack Vente (DDT complet)</strong> et le <strong>Pack Location</strong> —
                ainsi que chaque diagnostic à l'unité : <strong>amiante, plomb (CREP), électricité, gaz, termites, loi Carrez</strong>.
                Un seul contact, un seul devis, un seul rendez-vous : vous gagnez du temps et jusqu'à 30 % sur
                les tarifs à l'unité.
              </p>
              <p>
                Nos interventions sont garanties <strong>sous 48h</strong> à Paris intra-muros, dans les Hauts-de-Seine (92),
                Seine-Saint-Denis (93), Val-de-Marne (94), Yvelines (78), Essonne (91), Seine-et-Marne (77) et
                Val-d'Oise (95). Le rapport certifié est transmis le jour même de la visite, prêt à être remis à
                votre notaire ou annexé au bail de votre locataire.
              </p>
            </div>
          </div>
        </section>

        {/* ═════════ PACKS — Mis en avant ═════════ */}
        <section className="bg-gradient-to-br from-[#FFF7ED] to-[#FEF3C7] py-20 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#F97316]/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#F59E0B]/5 rounded-full translate-x-1/2 translate-y-1/2"></div>

          <div className="relative max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="inline-block bg-[#F97316] text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
                ★ Nos formules les plus populaires
              </span>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-[#0F3B5F] mb-3"
                style={{ fontFamily: "Barlow, sans-serif" }}
              >
                Les Packs Diagnostic Up Diag
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Réunissez tous vos diagnostics obligatoires en un seul rendez-vous — jusqu'à 30 % d'économies
                versus les diagnostics à l'unité.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {packs.map((pack) => (
                <Link
                  key={pack.id}
                  to={`/diagnostics/${pack.slug}`}
                  className="group bg-white rounded-3xl p-8 border-2 border-[#F59E0B] shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 block relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 bg-[#F59E0B] text-white text-xs font-bold px-4 py-1.5 rounded-bl-2xl">
                    ★ Le plus demandé
                  </div>

                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#F97316] to-[#F59E0B] rounded-2xl mb-5">
                    <i className={`${pack.icon} text-3xl text-white`}></i>
                  </div>

                  <h3
                    className="text-2xl md:text-3xl font-extrabold text-[#0F3B5F] mb-1 group-hover:text-[#F97316] transition-colors"
                    style={{ fontFamily: "Barlow, sans-serif" }}
                  >
                    {pack.name}
                  </h3>
                  <p className="text-sm text-gray-500 font-medium mb-4">{pack.fullName}</p>
                  <p className="text-gray-600 leading-relaxed mb-6">{pack.description}</p>

                  <ul className="flex flex-col gap-2 mb-6">
                    {pack.points.map((pt) => (
                      <li key={pt} className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="ri-checkbox-circle-fill text-[#16A34A] text-lg"></i>
                        <strong>{pt}</strong>
                      </li>
                    ))}
                  </ul>

                  <div className="flex items-center justify-between pt-6 border-t border-gray-100">
                    <div>
                      <div className="text-xs text-gray-400 uppercase tracking-wider">À partir de</div>
                      <div
                        className="text-3xl font-extrabold text-[#0F3B5F]"
                        style={{ fontFamily: "Barlow, sans-serif" }}
                      >
                        {pack.price}
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-2 px-5 py-3 bg-[#F97316] text-white rounded-xl font-bold group-hover:bg-orange-600 transition-colors">
                      Voir le pack <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ═════════ Pour quel projet ? ═════════ */}
        <section className="bg-white py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="inline-block bg-blue-50 text-[#0F3B5F] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
                Pour quel projet ?
              </span>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-[#0F3B5F] mb-3"
                style={{ fontFamily: "Barlow, sans-serif" }}
              >
                Quel diagnostic selon votre situation ?
              </h2>
              <p className="text-gray-500 max-w-2xl mx-auto">
                Chaque projet immobilier implique des diagnostics spécifiques. Identifiez les vôtres en un
                coup d'œil et recevez votre devis adapté.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Je vends */}
              <div className="bg-white rounded-2xl p-7 border border-gray-100 hover:border-orange-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col">
                <div className="w-14 h-14 flex items-center justify-center bg-orange-50 rounded-xl mb-5">
                  <i className="ri-home-4-line text-2xl text-[#F97316]"></i>
                </div>
                <h3
                  className="text-xl font-bold text-[#0F3B5F] mb-1"
                  style={{ fontFamily: "Barlow, sans-serif" }}
                >
                  Je vends
                </h3>
                <p className="text-xs text-gray-400 font-medium mb-4">Vente immobilière</p>
                <ul className="flex flex-col gap-2 mb-6 flex-grow">
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] mt-0.5"></i>DPE obligatoire
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] mt-0.5"></i>Amiante si avant 1997
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] mt-0.5"></i>Plomb si avant 1949
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] mt-0.5"></i>Élec/gaz si &gt; 15 ans
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] mt-0.5"></i>Loi Carrez (copro)
                  </li>
                </ul>
                <Link
                  to="/diagnostics/pack-vente"
                  className="inline-flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-[#F97316] text-[#F97316] rounded-xl font-bold hover:bg-[#F97316] hover:text-white transition-all"
                >
                  Pack Vente
                </Link>
              </div>

              {/* Je loue */}
              <div className="bg-white rounded-2xl p-7 border border-gray-100 hover:border-orange-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col">
                <div className="w-14 h-14 flex items-center justify-center bg-blue-50 rounded-xl mb-5">
                  <i className="ri-key-2-line text-2xl text-[#0F3B5F]"></i>
                </div>
                <h3
                  className="text-xl font-bold text-[#0F3B5F] mb-1"
                  style={{ fontFamily: "Barlow, sans-serif" }}
                >
                  Je loue
                </h3>
                <p className="text-xs text-gray-400 font-medium mb-4">Mise en location</p>
                <ul className="flex flex-col gap-2 mb-6 flex-grow">
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] mt-0.5"></i>DPE obligatoire
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] mt-0.5"></i>ERP état des risques
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] mt-0.5"></i>Plomb (CREP) si avant 1949
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] mt-0.5"></i>Loi Boutin (surface habitable)
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] mt-0.5"></i>Élec/gaz si ancien
                  </li>
                </ul>
                <Link
                  to="/diagnostics/pack-location"
                  className="inline-flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-[#0F3B5F] text-[#0F3B5F] rounded-xl font-bold hover:bg-[#0F3B5F] hover:text-white transition-all"
                >
                  Pack Location
                </Link>
              </div>

              {/* Je rénove */}
              <div className="bg-white rounded-2xl p-7 border border-gray-100 hover:border-orange-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col">
                <div className="w-14 h-14 flex items-center justify-center bg-green-50 rounded-xl mb-5">
                  <i className="ri-hammer-line text-2xl text-[#16A34A]"></i>
                </div>
                <h3
                  className="text-xl font-bold text-[#0F3B5F] mb-1"
                  style={{ fontFamily: "Barlow, sans-serif" }}
                >
                  Je rénove
                </h3>
                <p className="text-xs text-gray-400 font-medium mb-4">Travaux énergétiques</p>
                <ul className="flex flex-col gap-2 mb-6 flex-grow">
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] mt-0.5"></i>DPE avant / après travaux
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] mt-0.5"></i>Repérage amiante si travaux
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] mt-0.5"></i>Plomb avant rénovation
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] mt-0.5"></i>Éligible MaPrimeRénov'
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] mt-0.5"></i>Éco-PTZ & CEE
                  </li>
                </ul>
                <Link
                  to="/diagnostics/dpe"
                  className="inline-flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-[#16A34A] text-[#16A34A] rounded-xl font-bold hover:bg-[#16A34A] hover:text-white transition-all"
                >
                  DPE rénovation
                </Link>
              </div>

              {/* Je suis pro */}
              <div className="bg-white rounded-2xl p-7 border border-gray-100 hover:border-orange-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col">
                <div className="w-14 h-14 flex items-center justify-center bg-purple-50 rounded-xl mb-5">
                  <i className="ri-building-line text-2xl text-[#7B2D8B]"></i>
                </div>
                <h3
                  className="text-xl font-bold text-[#0F3B5F] mb-1"
                  style={{ fontFamily: "Barlow, sans-serif" }}
                >
                  Je suis pro
                </h3>
                <p className="text-xs text-gray-400 font-medium mb-4">Agence / copropriété</p>
                <ul className="flex flex-col gap-2 mb-6 flex-grow">
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] mt-0.5"></i>DPE collectif copropriété
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] mt-0.5"></i>Local commercial
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] mt-0.5"></i>Tarifs négociés volume
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] mt-0.5"></i>Facturation professionnelle
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] mt-0.5"></i>Gestionnaire dédié
                  </li>
                </ul>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-[#7B2D8B] text-[#7B2D8B] rounded-xl font-bold hover:bg-[#7B2D8B] hover:text-white transition-all"
                >
                  Nous contacter
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ═════════ Diagnostics à l'unité ═════════ */}
        <section className="bg-[#F8FAFC] py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="inline-block bg-blue-50 text-[#0F3B5F] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
                Diagnostics à l'unité
              </span>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-[#0F3B5F] mb-3"
                style={{ fontFamily: "Barlow, sans-serif" }}
              >
                L'ensemble de nos diagnostics
              </h2>
              <p className="text-gray-500 max-w-2xl mx-auto">
                Chaque diagnostic réglementaire est disponible à l'unité. DPE réalisé par nos diagnostiqueurs,
                autres diagnostics via notre réseau de partenaires certifiés Cofrac.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {unitServices.map((svc) => (
                <Link
                  key={svc.id}
                  to={`/diagnostics/${svc.slug}`}
                  className="group bg-white rounded-2xl p-7 border border-gray-100 hover:-translate-y-1 hover:border-orange-200 transition-all duration-300 block"
                >
                  {svc.badge && (
                    <span className="inline-block bg-[#F97316] text-white text-xs font-bold px-3 py-1 rounded-full mb-4">
                      {svc.badge}
                    </span>
                  )}
                  <div
                    className={`w-14 h-14 flex items-center justify-center rounded-xl mb-4 ${
                      svc.featured
                        ? "bg-gradient-to-br from-[#F97316] to-orange-600"
                        : "bg-[#0F3B5F]/8 group-hover:bg-[#0F3B5F]/12"
                    } transition-colors`}
                  >
                    <i className={`${svc.icon} text-2xl ${svc.featured ? "text-white" : "text-[#0F3B5F]"}`}></i>
                  </div>
                  <h3
                    className="text-xl font-bold text-[#0F3B5F] mb-1 group-hover:text-[#F97316] transition-colors"
                    style={{ fontFamily: "Barlow, sans-serif" }}
                  >
                    {svc.name}
                  </h3>
                  <p className="text-xs text-gray-400 font-medium mb-3">{svc.fullName}</p>
                  <p className="text-sm text-gray-500 leading-relaxed mb-5">{svc.description}</p>
                  <ul className="flex flex-col gap-1.5 mb-5">
                    {svc.points.map((pt) => (
                      <li key={pt} className="flex items-center gap-2 text-xs text-gray-500">
                        <i className="ri-checkbox-circle-fill text-[#16A34A]"></i>
                        {pt}
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                    <span
                      className="text-xl font-extrabold text-[#0F3B5F]"
                      style={{ fontFamily: "Barlow, sans-serif" }}
                    >
                      {svc.price}
                    </span>
                    <span className="text-sm font-semibold text-[#F97316] flex items-center gap-1">
                      En savoir plus <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ═════════ Pourquoi certifié Cofrac ? — SEO prose ═════════ */}
        <section className="bg-white py-20">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-8">
              <span className="inline-block bg-blue-50 text-[#0F3B5F] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
                Expertise & certifications
              </span>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-[#0F3B5F] mb-4"
                style={{ fontFamily: "Barlow, sans-serif" }}
              >
                Pourquoi choisir un diagnostiqueur certifié Cofrac ?
              </h2>
            </div>
            <div className="text-gray-700 leading-relaxed space-y-4 text-[15px]">
              <p>
                La <strong>certification Cofrac</strong> (Comité français d'accréditation) est une obligation légale pour
                réaliser les diagnostics immobiliers réglementaires en France. Elle garantit que le diagnostiqueur
                a suivi une formation agréée, qu'il maîtrise les normes techniques en vigueur et qu'il est soumis
                à des contrôles qualité périodiques. Sans certification Cofrac, un rapport de diagnostic est
                <strong> juridiquement nul</strong> et peut entraîner des poursuites contre le vendeur ou le bailleur.
              </p>
              <p>
                Chez Up Diag, nos diagnostiqueurs sont certifiés par des organismes indépendants reconnus
                (Bureau Veritas, Dekra, I.Cert), et chaque certification est <strong>renouvelée tous les 7 ans</strong> après
                examen. Nous disposons également d'une <strong>assurance Responsabilité Civile Professionnelle</strong> dédiée,
                obligatoire depuis la réforme DPE de juillet 2021 qui rend le diagnostic <em>opposable</em>.
              </p>
              <p>
                Que ce soit pour un <strong>DPE à Paris</strong>, un <strong>diagnostic amiante à Boulogne-Billancourt</strong>,
                un <strong>CREP plomb à Vincennes</strong> ou un <strong>pack vente complet à Versailles</strong>, nous garantissons
                des rapports conformes aux dernières normes (3CL-DPE 2021, NF X 46-020, XP C 16-600, NF P 45-500)
                et acceptés par l'ensemble des notaires franciliens.
              </p>
            </div>
          </div>
        </section>

        {/* ═════════ Calendrier réglementaire DPE ═════════ */}
        <section className="relative py-20 overflow-hidden bg-gradient-to-br from-[#0A2B47] via-[#0F3B5F] to-[#1A4A76]">
          <div
            className="absolute inset-0 opacity-5"
            style={{
              backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
              backgroundSize: "32px 32px",
            }}
          ></div>

          <div className="relative max-w-5xl mx-auto px-6">
            <div className="text-center mb-14">
              <span className="inline-flex items-center gap-2 bg-[#F97316]/15 border border-[#F97316]/30 text-[#FDBA74] text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-4">
                <i className="ri-calendar-line"></i>
                Calendrier réglementaire
              </span>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-white mb-3"
                style={{ fontFamily: "Barlow, sans-serif" }}
              >
                DPE 2025-2034 : le calendrier des interdictions
              </h2>
              <p className="text-white/70 max-w-2xl mx-auto">
                La loi Climat et Résilience impose un retrait progressif des passoires thermiques du marché
                locatif. Anticipez les échéances qui concernent votre bien.
              </p>
            </div>

            {/* Timeline */}
            <div className="relative">
              {/* Ligne verticale */}
              <div className="absolute left-6 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#F97316] via-[#F59E0B] to-green-400"></div>

              <div className="space-y-10">
                {[
                  {
                    date: "2022",
                    title: "Gel des loyers F & G",
                    desc: "Les loyers des logements classés F et G ne peuvent plus être augmentés (nouveau bail, renouvellement, indexation IRL).",
                    icon: "ri-lock-line",
                    color: "gold",
                    passed: true,
                  },
                  {
                    date: "2025",
                    title: "Interdiction location classe G",
                    desc: "Les logements classés G (consommation > 450 kWh/m²/an) ne peuvent plus être proposés à une nouvelle mise en location.",
                    icon: "ri-close-circle-line",
                    color: "orange",
                    active: true,
                  },
                  {
                    date: "2028",
                    title: "Interdiction location classe F",
                    desc: "Extension de l'interdiction aux logements classés F. Anticipez vos travaux de rénovation dès maintenant.",
                    icon: "ri-alarm-warning-line",
                    color: "orange",
                  },
                  {
                    date: "2034",
                    title: "Interdiction location classe E",
                    desc: "Derniers logements énergivores concernés. À terme, seuls les biens A à D seront autorisés à la location.",
                    icon: "ri-flag-line",
                    color: "green",
                  },
                ].map((e, i) => (
                  <div
                    key={i}
                    className={`relative flex flex-col md:flex-row gap-6 ${
                      i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                    }`}
                  >
                    {/* Bullet */}
                    <div className="absolute left-6 md:left-1/2 -translate-x-1/2 z-10">
                      <div
                        className={`w-12 h-12 rounded-full border-4 border-[#0A2B47] flex items-center justify-center ${
                          e.active
                            ? "bg-[#F97316] shadow-lg shadow-[#F97316]/50 animate-pulse"
                            : e.passed
                            ? "bg-white/20"
                            : "bg-white/10"
                        }`}
                      >
                        <i className={`${e.icon} text-white text-xl`}></i>
                      </div>
                    </div>

                    {/* Spacer */}
                    <div className="hidden md:block md:w-1/2"></div>

                    {/* Card */}
                    <div className="pl-20 md:pl-0 md:w-1/2 md:px-8">
                      <div
                        className={`bg-white/5 backdrop-blur-sm border rounded-2xl p-6 ${
                          e.active ? "border-[#F97316]" : "border-white/10"
                        }`}
                      >
                        <div
                          className={`text-3xl font-extrabold mb-2 ${
                            e.active ? "text-[#FDBA74]" : "text-white"
                          }`}
                          style={{ fontFamily: "Barlow, sans-serif" }}
                        >
                          {e.date}
                        </div>
                        <h3
                          className="text-lg font-bold text-white mb-2"
                          style={{ fontFamily: "Barlow, sans-serif" }}
                        >
                          {e.title}
                        </h3>
                        <p className="text-sm text-white/70 leading-relaxed">{e.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-12 text-center">
              <Link
                to="/diagnostics/dpe"
                className="inline-flex items-center gap-2 px-7 py-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap shadow-lg shadow-[#F97316]/30"
              >
                <i className="ri-fire-line"></i>
                Commander mon DPE dès 130 €
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* ═════════ Comparatif des formules ═════════ */}
        <section className="bg-[#F8FAFC] py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="inline-block bg-blue-50 text-[#0F3B5F] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
                Comparatif
              </span>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-[#0F3B5F] mb-3"
                style={{ fontFamily: "Barlow, sans-serif" }}
              >
                Quelle formule choisir ?
              </h2>
              <p className="text-gray-500 max-w-2xl mx-auto">
                Comparez en un coup d'œil les 3 façons de commander vos diagnostics chez Up Diag.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: "Diagnostic unitaire",
                  subtitle: "À la demande",
                  price: "Dès 80 €",
                  color: "gray",
                  link: "/diagnostics",
                  features: [
                    { label: "1 diagnostic à la fois", yes: true },
                    { label: "RDV sous 48h", yes: true },
                    { label: "Rapport certifié", yes: true },
                    { label: "Économies volume", yes: false },
                    { label: "Un seul RDV complet", yes: false },
                    { label: "DDT prêt notaire", yes: false },
                  ],
                  cta: "Voir les diagnostics",
                },
                {
                  title: "Pack Vente",
                  subtitle: "DDT complet",
                  price: "Dès 280 €",
                  color: "orange",
                  link: "/diagnostics/pack-vente",
                  featured: true,
                  badge: "★ Le plus populaire",
                  features: [
                    { label: "Tous diagnostics vente", yes: true },
                    { label: "RDV sous 48h", yes: true },
                    { label: "Rapport certifié", yes: true },
                    { label: "Jusqu'à -30 % sur l'unité", yes: true },
                    { label: "Un seul RDV complet", yes: true },
                    { label: "DDT prêt notaire", yes: true },
                  ],
                  cta: "Commander le pack",
                },
                {
                  title: "Pack Location",
                  subtitle: "Pour bailleurs",
                  price: "Dès 180 €",
                  color: "blue",
                  link: "/diagnostics/pack-location",
                  features: [
                    { label: "Tous diagnostics location", yes: true },
                    { label: "RDV sous 48h", yes: true },
                    { label: "Rapport certifié", yes: true },
                    { label: "Prix préférentiel", yes: true },
                    { label: "Un seul RDV complet", yes: true },
                    { label: "Conforme bail 2025", yes: true },
                  ],
                  cta: "Commander le pack",
                },
              ].map((plan) => (
                <div
                  key={plan.title}
                  className={`relative bg-white rounded-2xl p-8 border-2 transition-all hover:-translate-y-1 ${
                    plan.featured
                      ? "border-[#F59E0B] shadow-xl shadow-[#F59E0B]/10"
                      : "border-gray-100 hover:shadow-lg"
                  }`}
                >
                  {plan.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F59E0B] text-white text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap">
                      {plan.badge}
                    </div>
                  )}

                  <div className="mb-6">
                    <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">
                      {plan.subtitle}
                    </div>
                    <h3
                      className="text-2xl font-extrabold text-[#0F3B5F] mb-2"
                      style={{ fontFamily: "Barlow, sans-serif" }}
                    >
                      {plan.title}
                    </h3>
                    <div
                      className={`text-3xl font-extrabold ${
                        plan.featured ? "text-[#F97316]" : "text-[#0F3B5F]"
                      }`}
                      style={{ fontFamily: "Barlow, sans-serif" }}
                    >
                      {plan.price}
                    </div>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <i
                          className={`flex-shrink-0 mt-0.5 text-lg ${
                            f.yes ? "ri-checkbox-circle-fill text-[#16A34A]" : "ri-close-circle-line text-gray-300"
                          }`}
                        ></i>
                        <span className={f.yes ? "text-gray-700" : "text-gray-400 line-through"}>
                          {f.label}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={plan.link}
                    className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-bold transition-all ${
                      plan.featured
                        ? "bg-[#F97316] text-white hover:bg-orange-600 shadow-md"
                        : "border-2 border-[#0F3B5F] text-[#0F3B5F] hover:bg-[#0F3B5F] hover:text-white"
                    }`}
                  >
                    {plan.cta}
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═════════ FAQ générale diagnostics ═════════ */}
        <section className="bg-white py-20">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="inline-block bg-blue-50 text-[#0F3B5F] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
                FAQ
              </span>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-[#0F3B5F]"
                style={{ fontFamily: "Barlow, sans-serif" }}
              >
                Vos questions fréquentes
              </h2>
            </div>

            <div className="space-y-3">
              {[
                {
                  q: "Quels sont les diagnostics obligatoires pour vendre à Paris ?",
                  a: "Pour une vente à Paris, vous devez fournir : DPE (toujours obligatoire), diagnostic amiante si le permis de construire est antérieur au 1er juillet 1997, CREP plomb pour les biens d'avant 1949, diagnostic électricité et gaz si l'installation a plus de 15 ans, mesurage Loi Carrez en copropriété, et l'ERP (gratuit). Notre Pack Vente regroupe tous ces diagnostics en un seul rendez-vous.",
                },
                {
                  q: "Quel est le délai d'intervention en Île-de-France ?",
                  a: "Nous garantissons un délai d'intervention sous 48h à Paris et dans les 7 départements franciliens (92, 93, 94, 78, 91, 77, 95). En cas d'urgence (signature imminente), un RDV le jour même peut être possible selon la disponibilité de nos diagnostiqueurs.",
                },
                {
                  q: "Combien coûte un pack complet de diagnostics ?",
                  a: "Le Pack Vente démarre à 280 € TTC pour un appartement jusqu'à 50 m², et le Pack Location à 180 €. Ces tarifs incluent tous les diagnostics obligatoires + le déplacement en Île-de-France. Pour un diagnostic à l'unité, les prix débutent à 80 € (Loi Carrez) et 130 € (DPE).",
                },
                {
                  q: "Vos diagnostiqueurs sont-ils certifiés ?",
                  a: "Oui, tous nos diagnostiqueurs sont certifiés Cofrac (accréditation obligatoire par la loi) et disposent d'une Responsabilité Civile Professionnelle. Nos certifications sont renouvelées tous les 7 ans par des organismes indépendants : Bureau Veritas, Dekra, I.Cert.",
                },
                {
                  q: "Le DPE est-il vraiment opposable depuis 2021 ?",
                  a: "Oui, depuis le 1er juillet 2021, le DPE est juridiquement opposable. Le vendeur ou le bailleur engage sa responsabilité sur les informations transmises. En cas d'erreur significative, l'acquéreur ou le locataire peut demander des dommages-intérêts. C'est pourquoi il est essentiel de faire appel à un diagnostiqueur certifié.",
                },
                {
                  q: "Quand mes logements F et G seront-ils interdits à la location ?",
                  a: "Depuis 2025 : les logements G sont interdits à toute nouvelle mise en location. En 2028 : interdiction des F. En 2034 : interdiction des E. Anticipez en commandant un DPE à jour et en planifiant les travaux de rénovation énergétique éligibles à MaPrimeRénov', éco-PTZ et CEE.",
                },
              ].map((faq, i) => (
                <details
                  key={i}
                  className="group bg-white rounded-2xl border-2 border-gray-100 hover:border-orange-200 transition-all"
                  open={i === 0}
                >
                  <summary className="flex items-center justify-between gap-4 px-6 py-5 cursor-pointer list-none">
                    <span
                      className="font-bold text-[#0F3B5F] text-base pr-4"
                      style={{ fontFamily: "Barlow, sans-serif" }}
                    >
                      {faq.q}
                    </span>
                    <i className="ri-arrow-down-s-line text-2xl text-[#F97316] flex-shrink-0 transition-transform group-open:rotate-180"></i>
                  </summary>
                  <div className="px-6 pb-5 text-gray-600 leading-relaxed text-[15px] border-t border-gray-50 pt-4">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#0F3B5F] py-16">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2
              className="text-3xl font-extrabold text-white mb-4"
              style={{ fontFamily: "Barlow, sans-serif" }}
            >
              Besoin d'un devis personnalisé ?
            </h2>
            <p className="text-white/75 mb-8">
              Réponse sous 1h, intervention sous 48h dans toute l'Île-de-France.
            </p>
            <Link
              to="/devis"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap"
            >
              <i className="ri-file-list-3-line"></i> Obtenir mon devis gratuit
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
